import FilterIcon from '@mdi/svg/svg/filter-outline.svg?react';
import FilterAppliedIcon from '@mdi/svg/svg/filter.svg?react';
import React from 'react';
import styled from 'styled-components';
import { COLOR } from '../../styles';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { MDIIconButton } from '../MDIIconButton';
import { PopoverTooltip, usePopoverMenu } from '../PopoverTooltip';
import { Select } from '../Select';
import { TextField } from '../TextField';
import { type TableFilterProps } from './types';

export default function TableFilter(props: TableFilterProps) {
  const { filterVariant, filterValue, setFilterValue, localizations } = props;

  const [value, setValue] = React.useState(filterValue);

  React.useEffect(() => {
    setValue(filterValue);
  }, [filterValue]);

  const id = React.useId();

  const filterApplied = !!filterValue;

  const [filterPopover, toggleFilter] = usePopoverMenu({
    placement: 'bottom',
  });

  const onChange = React.useCallback(({ value }: { value: string }) => setValue(value), []);

  const onSave = () => {
    setFilterValue(value);
    toggleFilter();
  };
  const onClear = () => {
    setValue('');
  };

  const onCancel = () => {
    setValue(filterValue);
    toggleFilter();
  };

  const filterPopoverProps = React.useMemo(
    () => ({
      ...filterPopover,
      getTooltipProps: () => ({
        ...filterPopover.getTooltipProps(),
        style: {
          ...filterPopover.getTooltipProps().style,
          ...{ border: `1px solid ${COLOR['light-gray-light']}` },
        },
      }),
      getArrowProps: () => ({
        ...filterPopover.getArrowProps(),
        'data-popper-arrow': false,
      }),
    }),
    [filterPopover],
  );

  return (
    <TableFilterWrapper alignItems="center" width="auto">
      <MDIIconButton
        title={localizations?.filter ?? 'Filter'}
        onClick={toggleFilter}
        icon={filterApplied ? FilterAppliedIcon : FilterIcon}
        fill={COLOR['turquoise-dark']}
        iconSize={16}
        ref={filterPopover.setTriggerRef}
      />
      <PopoverTooltip {...filterPopoverProps} mode="light">
        <PopoverContent>
          {filterVariant === 'select' ? (
            <Select
              name={id}
              value={value}
              onChange={onChange}
              options={props.filterOptions}
              autoFocus
            />
          ) : (
            <TextField
              name={id}
              value={value as string}
              onChange={onChange}
              onClear={onClear}
              autoFocus
            />
          )}
          <Flex justifyContent="flex-end" gap="0.5rem">
            <Button isPrimary={false} onClick={onCancel}>
              {localizations?.cancel ?? 'Cancel'}
            </Button>
            <Button onClick={onSave}>{localizations?.apply ?? 'Apply'}</Button>
          </Flex>
        </PopoverContent>
      </PopoverTooltip>
    </TableFilterWrapper>
  );
}

const TableFilterWrapper = styled(Flex)`
  > button {
    display: flex;
    padding: 0;
  }
`;

const PopoverContent = styled.div`
  width: 168px;
`;
